
import * as React from "react"
import * as ProgressPrimitive from "@radix-ui/react-progress"
import { cn } from "@/lib/utils"

interface ProgressProps extends React.ComponentPropsWithoutRef<typeof ProgressPrimitive.Root> {
  indicatorClassName?: string;
}

const Progress = React.forwardRef<React.ElementRef<typeof ProgressPrimitive.Root>, ProgressProps>(
  ({ className, value, indicatorClassName, ...props }, ref) => {
    // Ensure value is always safe and within range
    const safeValue = React.useMemo(() => (
      typeof value === 'number' && !isNaN(value) ? Math.max(0, Math.min(100, value)) : 0
    ), [value]);

    // Create the indicator style transformation once
    const indicatorStyle = React.useMemo(() => ({
      transform: `translateX(-${100 - safeValue}%)`,
      width: "100%",
      transition: "transform 0.2s ease-in-out"
    }), [safeValue]);

    return (
      <ProgressPrimitive.Root
        ref={ref}
        className={cn(
          "relative h-4 w-full overflow-hidden rounded-full bg-secondary",
          className
        )}
        {...props}
      >
        <ProgressPrimitive.Indicator
          className={cn(
            "h-full w-full flex-1 bg-primary transition-all",
            indicatorClassName
          )}
          style={indicatorStyle}
        />
      </ProgressPrimitive.Root>
    );
  }
);

Progress.displayName = ProgressPrimitive.Root.displayName;

export { Progress };
